/**
 * Gating Component Types
 *
 * Types for tier-gating components following blur teaser pattern.
 * @see TEMPORAL_VS_BEHAVIORAL_ARCHITECTURE.md v1.6 - Tier-Gating UX Strategy
 */

import type { ReactNode } from 'react';
import type { ContentType, UserTier } from '@/lib/analytics/events/types';

export type { UserTier };

/**
 * Required tier for gated content
 */
export type RequiredTier = 'drama_plus' | 'dramax';

/**
 * Blur intensity levels
 */
export type BlurIntensity = 'light' | 'heavy';

/**
 * Gating behavior types
 */
export type GatingBehavior = 'blur' | 'redirect';

/**
 * Tier hierarchy for access comparison
 */
export const TIER_HIERARCHY: Record<UserTier, number> = {
  anonymous: 0,
  free: 1,
  drama_plus: 2,
  dramax: 3,
};

/**
 * Blur amounts in pixels
 */
export const BLUR_AMOUNTS: Record<BlurIntensity, number> = {
  light: 6,
  heavy: 12,
};

/**
 * Tier display names for UI
 */
export const TIER_DISPLAY_NAMES: Record<RequiredTier, string> = {
  drama_plus: 'Drama+',
  dramax: 'DramaX',
};

/**
 * Feature keys that map to feature-specific i18n copy in features.json gating section.
 * Each key has corresponding _title, _body, and _cta entries.
 */
export type GatingFeatureKey =
  | 'pattern_spotlight'
  | 'transformation_insight_free'
  | 'transformation_insight_plus'
  | 'quota_free'
  | 'feature_preview';

/**
 * Props for GatedSection component
 */
export interface GatedSectionProps {
  /** Minimum tier required to view content */
  requiredTier: RequiredTier;

  /** User's current tier (optional - uses useEntitlements if not provided) */
  currentTier?: UserTier;

  /** Callback when user clicks upgrade CTA */
  onUpgrade: () => void;

  /** Gating behavior: 'blur' shows teaser, 'redirect' navigates away */
  behavior?: GatingBehavior;

  /** Blur intensity (only for behavior='blur') */
  blurIntensity?: BlurIntensity;

  /** Teaser text shown over blurred content */
  teaser?: string;

  /** Number of items being gated (for "Unlock X more" copy) */
  gatedCount?: number;

  /** Content type for analytics tracking */
  contentType: ContentType;

  /** Screen name for analytics tracking */
  screen: string;

  /** Content to render (blurred or visible based on tier) */
  children: ReactNode;

  /** Test ID for E2E testing */
  testID?: string;

  /** Feature-specific i18n key passed to UpgradePrompt (behavior='redirect' only) */
  featureKey?: GatingFeatureKey;

  /** Price string injected into CTA {price} placeholder (behavior='redirect' only) */
  price?: string;
}

/**
 * Props for UpgradePrompt component (redirect behavior)
 */
export interface UpgradePromptProps {
  targetTier: RequiredTier;
  onUpgrade: () => void;
  testID?: string;
  /** Feature-specific i18n key; resolves title/body/cta from features.json gating section */
  featureKey?: GatingFeatureKey;
  /** Price string injected into CTA {price} placeholder */
  price?: string;
}